import { AGENCIAS_DIRECTAS } from "../constants/permisos";
import { keyDeFactura, resolverFechaFactura } from "./comisionEdificiosUtil";

/**
 * Fuente ÚNICA del % de comisión de agencia de una venta (Fase A0 — unifica la lógica antes
 * duplicada en `calcularImporteNetoIntermediario`/`calcularPorcentajeComisionIntermediario`
 * (calcularComisionIntermediario.js) y `calculateAgencyCommission` (calculateComission.js)).
 *
 * Resolución: intermediario = `cliente.agencia` (regla 10.7; el override por venta OMNET se retiró en
 * C-2, 7.22 — OMNET corre por descuento). % = `intermediario.comision`, salvo que la fecha de factura
 * caiga en la ventana `comision_2`
 * (`comision_2_fechainicio`/`comision_2_fechafin`, comparación por instante absoluto TZ-agnóstico,
 * ver 7.17) → `comision_2`; `AGENCIAS_DIRECTAS` [16,32,60] → 0.
 *
 * Shape-agnostic (mismo criterio que `getApxMonto`): `clientes`/`intermediarios` pueden venir como
 * ARRAY (con filtro por `pais`, como los pasa calcularComisionIntermediario.js) o como MAPA keyed
 * por id (sin filtro de país porque ya viene país-scopeado, como los pasa calculateComission.js).
 *
 * Devuelve el porcentaje (ej. 15 para 15%); 0 si no resuelve cliente/agencia/intermediario o si es
 * agencia directa. NO aplica el descuento de acuerdo ni el override de BTL: eso queda en las
 * funciones llamadoras (comportamiento idéntico al previo al refactor).
 */
export const resolverPorcentajeAgencia = (factura, clientes, intermediarios, pais, pctMap = null) => {
  // A3 (7.22): si la venta tiene el % congelado (foto), devolverlo tal cual (aplicado sobre la base
  // viva por el wrapper). Sin foto (o pctMap null) → resolver en vivo (rama de abajo, sin cambios).
  const congelado = pctMap?.get(keyDeFactura(factura));
  if (congelado) return congelado.agencia;
  // Intermediario = la agencia del cliente (regla 10.7). El override por venta (OMNET) se retiró en C-2
  // (7.22): OMNET corre por `descuento_acuerdo_pct`, no por override; la columna quedó inerte.
  const cliente = Array.isArray(clientes)
    ? clientes.find((c) => c.id === factura.client_id && c.pais === pais)
    : clientes?.[factura.client_id];
  if (!cliente || !cliente.agencia) return 0;
  const intermediarioId = parseInt(cliente.agencia, 10);
  if (isNaN(intermediarioId)) return 0;
  const intermediario = Array.isArray(intermediarios)
    ? intermediarios.find((i) => i.id === intermediarioId && i.pais === pais)
    : intermediarios?.[intermediarioId];
  if (!intermediario) return 0;
  const fechaFactura = new Date(resolverFechaFactura(factura));
  let porcentaje = intermediario.comision || 0;
  // Ventana comision_2 por FECHA ABSOLUTA (TZ-agnóstico). Antes comparaba por año (getFullYear) →
  // bug TZ: `new Date('YYYY-01-01').getFullYear()` devuelve el año anterior en TZ Chile.
  if (intermediario.comision_2 !== null && intermediario.comision_2_fechainicio && intermediario.comision_2_fechafin) {
    const inicio = new Date(intermediario.comision_2_fechainicio);
    const fin = new Date(intermediario.comision_2_fechafin);
    if (fechaFactura >= inicio && fechaFactura <= fin) {
      porcentaje = intermediario.comision_2 || 0;
    }
  }
  // Ventas directas: agencias 16 (Perú) y 32 (Chile) = 0%
  if (AGENCIAS_DIRECTAS.includes(intermediarioId)) {
    porcentaje = 0;
  }
  return porcentaje;
};
